import * as Yup from 'yup'

import {
  AttributeCategory,
  AttributeType,
  MultiValueAttributeType,
  SingleValueAttributeType,
} from 'constants/types'

import {
  Address,
  Option,
  ProfileAttribute,
  User,
  UserProfile,
  CatalogItem,
  socialMediaRegex,
} from 'shared'
import { socialMediaRegexErrorMessage } from 'utils/validations'
import { AddressFieldInfo } from 'utils/hooks/useAddress'

type UpdatedUser = Omit<UserProfile, 'isVerified' | 'isEmailVerified' | 'id'>

export interface ProfileFormValues extends UpdatedUser {
  address: Address | null
  communityGuidesAccepted: boolean
  willingAccepted: boolean
  TnCAccepted: boolean
  typedSignature: string
  english: { label: string; value: boolean } | null
  getValueByAttribute(
    attribute: SingleValueAttributeType | MultiValueAttributeType,
    category?: AttributeCategory,
    activeAttributes?: ProfileAttribute[]
  ): Option | null
  getValuesByAttribute(
    attribute: SingleValueAttributeType | MultiValueAttributeType,
    category?: AttributeCategory,
    activeAttributes?: ProfileAttribute[]
  ): Option[]
  setAttributeValue(data: {
    attribute: AttributeType
    category: AttributeCategory
    value: CatalogItem | CatalogItem[] | string | null
  }): ProfileFormValues
  validationErrors: Record<AttributeType, string>
}

export const englishOptions = [
  {
    label: 'Yes',
    value: true,
  },
  {
    label: 'No',
    value: false,
  },
]

export const getInitialValues = (user: User | null): ProfileFormValues => {
  const languages = user?.profile?.attributes?.find(
    (el) => el.type === 'languages'
  )?.value as CatalogItem[]
  return {
    firstName: user?.profile.firstName || '',
    lastName: user?.profile.lastName || '',
    email: user?.profile.email || '',
    phoneNumber: user?.profile.phoneNumber || '',
    birthDate: user?.profile.birthDate || null,
    address: user?.profile.address || {
      city: '',
      state: '',
      postalCode: '',
      address: '',
      address2: '',
    },
    instagramHandle: user?.profile.instagramHandle || '',
    english: languages?.find((el) => el.text.toLowerCase() === 'english')
      ? {
          label: 'Yes',
          value: true,
        }
      : null,
    discordHandle: user?.profile?.discordHandle || '',
    profileImageUrl: user?.profile.profileImageUrl || '',
    tiktokHandle: user?.profile.tiktokHandle || '',
    attributes: user?.profile.attributes || [],
    isOnboardingCompleted: user?.profile?.isOnboardingCompleted || false,
    communityGuidesAccepted: false,
    willingAccepted: false,
    TnCAccepted: false,
    typedSignature: '',
    getValueByAttribute: (
      attribute: MultiValueAttributeType | SingleValueAttributeType,
      category?: AttributeCategory,
      activeAttributes?: ProfileAttribute[]
    ) => {
      const attributes = activeAttributes || user?.profile.attributes || []
      const activeAttribute = attributes
        .filter(({ category: itemCategory }) =>
          category ? itemCategory === category : true
        )
        .find(({ type: itemAttribute }) => itemAttribute === attribute)
        ?.value as CatalogItem

      if (!activeAttribute) return null

      return {
        value: activeAttribute.code,
        label: activeAttribute.text,
      }
    },
    getValuesByAttribute: (
      attribute: MultiValueAttributeType | SingleValueAttributeType,
      category?: AttributeCategory,
      activeAttributes?: ProfileAttribute[]
    ) => {
      const attributes = activeAttributes || user?.profile.attributes || []
      const activeAttribute = attributes
        .filter(({ category: itemCategory }) =>
          category ? itemCategory === category : true
        )
        .find(({ type }) => type === attribute)?.value as CatalogItem[]

      if (!activeAttribute?.length) return []

      return activeAttribute.map((el) => ({
        value: el.code,
        label: el.text,
      }))
    },
    setAttributeValue({ attribute, category, value }) {
      const itemIdx = this.attributes.findIndex(
        ({ category: itemCategory, type }) => {
          return itemCategory === category && type === attribute
        }
      )
      if (itemIdx === -1) {
        this.attributes.push({
          category,
          type: attribute,
          value: value,
        } as ProfileAttribute)
      } else {
        this.attributes[itemIdx].value = value
      }
      return this
    },
    validationErrors: {
      fashion_brands: '',
      beauty_brands: '',
      video_types: '',
      level_of_interest: '',
      hair_color: '',
      hair_type: '',
      hair_thickness: '',
      hobbies: '',
      preferred_pronouns: '',
      depth: '',
      undertone: '',
      height: '',
      skin_type: '',
      gender: '',
      resident: '',
      ethnicity: '',
      dress_size: '',
      eye_color: '',
      pants_size: '',
      shoe_size: '',
      languages: '',
    },
  }
}

export const reasonsToJoinValidationSchema = Yup.object().shape({
  attributes: Yup.array()
    .of(
      Yup.object().shape({
        type: Yup.string().required(),
        category: Yup.string().required(),
        value: Yup.mixed().nullable(),
      })
    )
    .test({
      name: 'reasons_to_join',
      test: (attributes, testContext) => {
        return !attributes?.find(({ type, value }) => {
          return (
            type === 'reasons_to_join' && Array.isArray(value) && value?.length
          )
        })
          ? testContext.createError({
              type: 'validationErrors.reasons_to_join',
              path: 'validationErrors.reasons_to_join',
              message: 'Select at least one answer',
            })
          : true
      },
    })
    .test({
      name: 'hear_about_us',
      test: (attributes, testContext) => {
        return !attributes?.find(({ type, value }) => {
          return (
            type === 'hear_about_us' && Array.isArray(value) && value?.length
          )
        })
          ? testContext.createError({
              type: 'validationErrors.hear_about_us',
              path: 'validationErrors.hear_about_us',
              message: 'Select at least one answer',
            })
          : true
      },
    })
    .test({
      name: 'hobbies',
      test: (attributes, testContext) => {
        return !attributes?.find(
          ({ type, value }) => type === 'hobbies' && value
        )
          ? testContext.createError({
              type: 'validationErrors.hobbies',
              path: 'validationErrors.hobbies',
              message: 'Hobbies are required',
            })
          : true
      },
    })
    .test({
      name: 'reasons_to_join_length',
      test: (attributes, testContext) => {
        return !attributes?.find(({ type, value }) => {
          return (
            type === 'reasons_to_join' &&
            Array.isArray(value) &&
            value?.length <= 3
          )
        })
          ? testContext.createError({
              type: 'validationErrors.reasons_to_join_length',
              path: 'validationErrors.reasons_to_join',
              message: 'Select up to three answers',
            })
          : true
      },
    }),
})

export const generalValidationSchema = (
  addressField: AddressFieldInfo,
  isUkValidation: boolean
) => {
  return Yup.object().shape({
    firstName: Yup.string().required().label('First name'),
    lastName: Yup.string().required().label('Last name'),
    email: Yup.string().email().required().label('Email'),
    phoneNumber: Yup.string()
      .required('Phone number is required')
      .min(10, 'Must contain at least 10 characters')
      .when('address.country', {
        is: (country: 'UK' | 'US') => country === 'UK',
        then: (schema) =>
          schema.matches(/^\+44\d{10}$/, 'Invalid UK phone number.'),
        otherwise: (schema) =>
          schema
            .matches(/^\d+$/, 'Invalid US phone number.')
            .typeError('Only numbers allowed'),
      })
      .label('Phone number'),
    birthDate: Yup.string().required().label('Date of birth'),
    address: Yup.object().shape({
      country: Yup.string().required().label('Country'),
      address: Yup.string()
        .min(5, 'Must contain at least 5 characters')
        .max(100, 'Must contain less than 100 characters')
        .required()
        .label(addressField.address.errorLabel),
      address2: Yup.string()
        .optional()
        .max(100, 'Must contain less than 100 characters')
        .label(addressField.address?.errorLabel || 'Unit Number'),
      city: Yup.string()
        .min(2, 'Must contain at least 2 characters')
        .max(50, 'Must contain less than 50 characters')
        .required()
        .label(addressField.city.errorLabel),
      postalCode: Yup.string()
        .required()
        .label(addressField.postalCode.errorLabel),
      state: isUkValidation
        ? Yup.string().label(addressField.state.errorLabel)
        : Yup.string().required().label(addressField.state.errorLabel),
    }),
  })
}

export const socialValidationSchema = Yup.object().shape({
  instagramHandle: Yup.string()
    .matches(socialMediaRegex, socialMediaRegexErrorMessage)
    .required('Instagram handle is required'),
  discordHandle: Yup.string()
    .matches(socialMediaRegex, socialMediaRegexErrorMessage)
    .label('Discord user name'),
  tiktokHandle: Yup.string()
    .matches(socialMediaRegex, socialMediaRegexErrorMessage)
    .label('Tiktok user name'),
})

export const aboutValidationSchema = Yup.object().shape({
  english: Yup.object()
    .shape({
      label: Yup.string().required(),
      value: Yup.string().required(),
    })
    .required()
    .label('English'),
  attributes: Yup.array()
    .of(
      Yup.object().shape({
        type: Yup.string().required(),
        category: Yup.string().required(),
        value: Yup.mixed().nullable(),
      })
    )
    .test({
      name: 'gender',
      test: (attributes, testContext) => {
        return !attributes?.find(
          ({ type, value }) => type === 'gender' && value
        )
          ? testContext.createError({
              type: 'validationErrors.gender',
              path: 'validationErrors.gender',
              message: 'Gender is required',
            })
          : true
      },
    })
    .test({
      name: 'resident',
      test: (attributes, testContext) => {
        return !attributes?.find(
          ({ type, value }) => type === 'resident' && value
        )
          ? testContext.createError({
              type: 'validationErrors.resident',
              path: 'validationErrors.resident',
              message: 'This is a required field',
            })
          : true
      },
    })
    .test({
      name: 'preferred_pronouns',
      test: (attributes, testContext) => {
        return !attributes?.find(
          ({ type, value }) => type === 'preferred_pronouns' && value
        )
          ? testContext.createError({
              type: 'validationErrors.preferred_pronouns',
              path: 'validationErrors.preferred_pronouns',
              message: 'Preferred pronouns is required',
            })
          : true
      },
    })
    .test({
      name: 'ethnicity',
      test: (attributes, testContext) => {
        return !attributes?.find(
          ({ type, value }) =>
            type === 'ethnicity' && Array.isArray(value) && value?.length
        )
          ? testContext.createError({
              type: 'validationErrors.ethnicity',
              path: 'validationErrors.ethnicity',
              message: 'Ethnicity is required',
            })
          : true
      },
    })
    .min(1, 'Select at least one attribute'),
})

export const contentValidationSchema = Yup.object().shape({
  attributes: Yup.array()
    .of(
      Yup.object().shape({
        type: Yup.string().required(),
        category: Yup.string().required(),
        value: Yup.mixed().nullable(),
      })
    )
    .test({
      name: 'level_of_interest',
      exclusive: false,
      params: { type: 'level_of_interest' },
      message: '${type} must be selected',
      test: (attributes, testContext) => {
        return !attributes?.find(({ type }) => type === 'level_of_interest')
          ? testContext.createError({
              type: 'validationErrors.level_of_interest',
              path: 'validationErrors.level_of_interest',
              message: 'Experience is required',
            })
          : true
      },
    })
    .required(),
})

export const agreementValidationSchema = Yup.object().shape({
  communityGuidesAccepted: Yup.bool().oneOf([true], 'Field must be checked'),
  TnCAccepted: Yup.bool().oneOf([true], 'Field must be checked'),
  willingAccepted: Yup.bool().oneOf([true], 'Field must be checked'),
})

// not used anymore
export const beautyValidationSchema = Yup.object().shape({
  attributes: Yup.array()
    .of(
      Yup.object().shape({
        type: Yup.string().required(),
        category: Yup.string().required(),
        value: Yup.mixed().nullable(),
      })
    )
    .test({
      name: 'depth',
      test: (attributes, testContext) => {
        return !attributes?.find(({ type, value }) => type === 'depth' && value)
          ? testContext.createError({
              type: 'validationErrors.depth',
              path: 'validationErrors.depth',
              message: 'Skin tone is required',
            })
          : true
      },
    })
    .test({
      name: 'undertone',
      test: (attributes, testContext) => {
        return !attributes?.find(
          ({ type, value }) => type === 'undertone' && value
        )
          ? testContext.createError({
              type: 'validationErrors.undertone',
              path: 'validationErrors.undertone',
              message: 'Undertone is required',
            })
          : true
      },
    })
    .test({
      name: 'skin_type',
      test: (attributes, testContext) => {
        return !attributes?.find(
          ({ type, value }) => type === 'skin_type' && value
        )
          ? testContext.createError({
              type: 'validationErrors.skin_type',
              path: 'validationErrors.skin_type',
              message: 'Skin type is required',
            })
          : true
      },
    })
    .test({
      name: 'hair_color',
      test: (attributes, testContext) => {
        return !attributes?.find(
          ({ type, value }) => type === 'hair_color' && value
        )
          ? testContext.createError({
              type: 'validationErrors.hair_color',
              path: 'validationErrors.hair_color',
              message: 'Hair color is required',
            })
          : true
      },
    })
    .test({
      name: 'hair_type',
      test: (attributes, testContext) => {
        return !attributes?.find(
          ({ type, value }) => type === 'hair_type' && value
        )
          ? testContext.createError({
              type: 'validationErrors.hair_type',
              path: 'validationErrors.hair_type',
              message: 'Hair type is required',
            })
          : true
      },
    })
    .test({
      name: 'hair_thickness',
      test: (attributes, testContext) => {
        return !attributes?.find(
          ({ type, value }) => type === 'hair_thickness' && value
        )
          ? testContext.createError({
              type: 'validationErrors.hair_thickness',
              path: 'validationErrors.hair_thickness',
              message: 'Hair thickness is required',
            })
          : true
      },
    })
    .test({
      name: 'eye_color',
      test: (attributes, testContext) => {
        return !attributes?.find(
          ({ type, value }) => type === 'eye_color' && value
        )
          ? testContext.createError({
              type: 'validationErrors.eye_color',
              path: 'validationErrors.eye_color',
              message: 'Eye color is required',
            })
          : true
      },
    }),
})

// not used anymore
export const sizesValidationSchema = Yup.object().shape({
  attributes: Yup.array()
    .of(
      Yup.object().shape({
        type: Yup.string().required(),
        category: Yup.string().required(),
        value: Yup.mixed().nullable(),
      })
    )
    .test({
      name: 'pants_size_w',
      test: (attributes, testContext) => {
        return !attributes?.find(
          ({ type, value }) => type === 'pants_size_w' && value
        )
          ? testContext.createError({
              type: 'validationErrors.pants_size_w',
              path: 'validationErrors.pants_size_w',
              message: 'Women bottoms / pants size is required',
            })
          : true
      },
    })
    .test({
      name: 'pants_size_m',
      test: (attributes, testContext) => {
        return !attributes?.find(
          ({ type, value }) => type === 'pants_size_m' && value
        )
          ? testContext.createError({
              type: 'validationErrors.pants_size_m',
              path: 'validationErrors.pants_size_m',
              message: 'Men bottoms / pants size is required',
            })
          : true
      },
    })
    .test({
      name: 'tops_size',
      test: (attributes, testContext) => {
        return !attributes?.find(
          ({ type, value }) => type === 'tops_size' && value
        )
          ? testContext.createError({
              type: 'validationErrors.tops_size',
              path: 'validationErrors.tops_size',
              message: 'Size in tops is required',
            })
          : true
      },
    })
    .test({
      name: 'suits_size',
      test: (attributes, testContext) => {
        return !attributes?.find(
          ({ type, value }) => type === 'suits_size' && value
        )
          ? testContext.createError({
              type: 'validationErrors.suits_size',
              path: 'validationErrors.suits_size',
              message: 'Suits size is required',
            })
          : true
      },
    })
    .test({
      name: 'shoe_size_new',
      test: (attributes, testContext) => {
        return !attributes?.find(
          ({ type, value }) => type === 'shoe_size_new' && value
        )
          ? testContext.createError({
              type: 'validationErrors.shoe_size_new',
              path: 'validationErrors.shoe_size_new',
              message: 'Shoe size is required',
            })
          : true
      },
    })
    .test({
      name: 'dress_size',
      test: (attributes, testContext) => {
        return !attributes?.find(
          ({ type, value }) => type === 'dress_size' && value
        )
          ? testContext.createError({
              type: 'validationErrors.dress_size',
              path: 'validationErrors.dress_size',
              message: 'Dress size is required',
            })
          : true
      },
    })
    .test({
      name: 'foot_width',
      test: (attributes, testContext) => {
        return !attributes?.find(
          ({ type, value }) => type === 'foot_width' && value
        )
          ? testContext.createError({
              type: 'validationErrors.foot_width',
              path: 'validationErrors.foot_width',
              message: 'Foot width is required',
            })
          : true
      },
    })
    .test({
      name: 'pants_type',
      test: (attributes, testContext) => {
        return !attributes?.find(
          ({ type, value }) => type === 'pants_type' && value
        )
          ? testContext.createError({
              type: 'validationErrors.pants_type',
              path: 'validationErrors.pants_type',
              message: 'Pants type is required',
            })
          : true
      },
    })
    .test({
      name: 'ear_piercings',
      test: (attributes, testContext) => {
        return !attributes?.find(
          ({ type, value }) => type === 'ear_piercings' && value
        )
          ? testContext.createError({
              type: 'validationErrors.ear_piercings',
              path: 'validationErrors.ear_piercings',
              message: 'Ear piercings amount is required',
            })
          : true
      },
    })
    .test({
      name: 'height',
      test: (attributes, testContext) => {
        return !attributes?.find(
          ({ type, value }) => type === 'height' && value
        )
          ? testContext.createError({
              type: 'validationErrors.height',
              path: 'validationErrors.height',
              message: 'Height is required',
            })
          : true
      },
    })
    .test({
      name: 'weight',
      test: (attributes, testContext) => {
        return !attributes?.find(
          ({ type, value }) => type === 'weight' && value
        )
          ? testContext.createError({
              type: 'validationErrors.weight',
              path: 'validationErrors.weight',
              message: 'Weight is required',
            })
          : true
      },
    })
    .test({
      name: 'bra_band_size',
      test: (attributes, testContext) => {
        return !attributes?.find(
          ({ type, value }) => type === 'bra_band_size' && value
        )
          ? testContext.createError({
              type: 'validationErrors.bra_band_size',
              path: 'validationErrors.bra_band_size',
              message: 'Bra band size is required',
            })
          : true
      },
    })
    .test({
      name: 'bra_cup_size',
      test: (attributes, testContext) => {
        return !attributes?.find(
          ({ type, value }) => type === 'bra_cup_size' && value
        )
          ? testContext.createError({
              type: 'validationErrors.bra_cup_size',
              path: 'validationErrors.bra_cup_size',
              message: 'Bra cup size is required',
            })
          : true
      },
    })
    .test({
      name: 'shapewear_size',
      test: (attributes, testContext) => {
        return !attributes?.find(
          ({ type, value }) => type === 'shapewear_size' && value
        )
          ? testContext.createError({
              type: 'validationErrors.shapewear_size',
              path: 'validationErrors.shapewear_size',
              message: 'Shapewear size is required',
            })
          : true
      },
    })
    .test({
      name: 'body_type',
      test: (attributes, testContext) => {
        return !attributes?.find(
          ({ type, value }) =>
            type === 'body_type' && Array.isArray(value) && value?.length
        )
          ? testContext.createError({
              type: 'validationErrors.body_type',
              path: 'validationErrors.body_type',
              message: 'Body types are required',
            })
          : true
      },
    }),
})
